import { Component, OnDestroy, OnInit } from '@angular/core';
import {
   catchError,
   finalize,
   forkJoin,
   of,
   Subject,
   switchMap,
   takeUntil,
   throwError
} from 'rxjs';
import { BillingService } from '../../services/billing.service';
import { BillingContact } from '../../services/response/xero/billing-contact';
import { UpdateBillingContactRequest } from '../../services/request/xero/update-billing-contact-request';
import { XeroInvoiceRow } from '../../services/response/xero/xero-invoice-row';
import { PropertyStoreService } from '../../services/stores/property-store.service';
import { MessageService } from 'primeng/api';
import { DividerModule } from 'primeng/divider';
import { BillingDetailsComponent } from './billing-details/billing-details.component';
import { BillingHistoryComponent } from './billing-history/billing-history.component';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ToastModule } from 'primeng/toast';
import { CommonModule } from '@angular/common';
import { Property } from '../../domain/property';
import { PanelModule } from 'primeng/panel';
import { TabViewModule } from 'primeng/tabview';
import { BillingCurrentPlanComponent } from './billing-current-plan/billing-current-plan.component';
import { PropertyPricingTierDetails } from '../../services/response/PropertyPricingTierDetails';

@Component({
   selector: 'app-billing-page',
   templateUrl: './billing-page.component.html',
   styleUrls: ['./billing-page.component.scss'],
   standalone: true,
   imports: [
      DividerModule,
      CommonModule,
      BillingDetailsComponent,
      BillingHistoryComponent,
      ProgressSpinnerModule,
      ToastModule,
      PanelModule,
      TabViewModule,
      BillingCurrentPlanComponent
   ],
   providers: [MessageService]
})
export class BillingPageComponent implements OnInit, OnDestroy {
   loading = false;
   saving = false;

   propertyId!: number;
   pricingTier: PropertyPricingTierDetails | null = null;
   currentProperty: Property | null = null;
   contact: BillingContact | null = null;
   invoices: XeroInvoiceRow[] = [];
   private destroy$ = new Subject<void>();

   constructor(
      private billing: BillingService,
      private propStore: PropertyStoreService,
      private messages: MessageService
   ) {}

   ngOnInit(): void {
      this.propStore.currentProperty
         .pipe(
            takeUntil(this.destroy$),
            switchMap((p) => {
               this.currentProperty = p ?? null;
               if (!p?.id) return of(null);
               this.propertyId = p.id;
               this.loading = true;

               const contact$ = this.billing.getBillingContact(p.id).pipe(
                  catchError((err) => {
                     if (err?.status === 404) return of(null);
                     return throwError(() => err);
                  })
               );

               const invoices$ = this.billing.getInvoicesAll(p.id).pipe(
                  catchError(() =>
                     of(
                        [] as
                           | XeroInvoiceRow[]
                           | {
                                Paid: XeroInvoiceRow[];
                                Outstanding: XeroInvoiceRow[];
                             }
                     )
                  )
               );

               const pricingTier$ = this.billing
                  .getPricingTier(p.id)
                  .pipe(catchError(() => of(null)));

               return forkJoin({
                  contact: contact$,
                  invoices: invoices$,
                  pricing: pricingTier$
               }).pipe(finalize(() => (this.loading = false)));
            })
         )
         .subscribe({
            next: (data) => {
               if (!data) return;

               this.contact = data.contact;

               this.pricingTier = data.pricing;

               if (!this.contact) {
                  this.invoices = [];
                  return;
               }

               const inv = Array.isArray(data.invoices)
                  ? data.invoices
                  : [
                       ...(data.invoices.Paid ?? []),
                       ...(data.invoices.Outstanding ?? [])
                    ];

               this.invoices = inv.sort(
                  (a, b) =>
                     (b.date ? Date.parse(b.date) : 0) -
                     (a.date ? Date.parse(a.date) : 0)
               );
            },
            error: () =>
               this.messages.add({
                  severity: 'error',
                  summary: 'Load failed',
                  detail: 'Could not load billing data.'
               })
         });
   }

   onSaveContact(payload: UpdateBillingContactRequest) {
      if (!this.propertyId) return;
      this.saving = true;

      this.billing
         .updateBillingContact(this.propertyId, payload)
         .pipe(finalize(() => (this.saving = false)))
         .subscribe({
            next: () => {
               this.messages.add({
                  severity: 'success',
                  summary: 'Billing Updated',
                  detail: 'Your billing details have been updated.'
               });

               if (!this.propertyId) return;

               this.billing.getBillingContact(this.propertyId).subscribe({
                  next: (c) => (this.contact = c),
                  error: () =>
                     this.messages.add({
                        severity: 'warn',
                        summary: 'Reload failed',
                        detail: 'Saved, but refresh failed.'
                     })
               });
            },
            error: () => {
               this.messages.add({
                  severity: 'error',
                  summary: 'Save failed',
                  detail: 'Could not update billing details.'
               });
            }
         });
   }

   onDownloadInvoice(inv: XeroInvoiceRow, download = true) {
      this.billing.getInvoicePdf(inv.invoiceId, download).subscribe({
         next: (res) =>
            this.billing.openBlob(res, `${inv.invoiceNumber || inv.invoiceId}.pdf`),
         error: () =>
            this.messages.add({
               severity: 'error',
               summary: 'Download failed',
               detail: 'Could not download invoice.'
            })
      });
   }

   ngOnDestroy(): void {
      this.destroy$.next();
      this.destroy$.complete();
   }
}
